import {
    artifactKeysOf,
    isArtifactElement,
    isArtifactFile,
    latestArtifactVersions,
    resolveLatestArtifactId
} from '@/lib/artifacts';
import { isInlineApp } from '@/components/DMMcpApps/inlineApps';
import { prefetchMcpAppContent } from '@/components/DMMcpApps/useMcpAppContent';
import { referencedDashboardsOf } from '@/lib/dashboardReferences';
import { isEmbeddedSidebar } from '@/lib/embedded-sidebar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import {
    ICustomElement,
    sideViewState,
    useChatMessages,
    useConfig
} from '@chainlit/react-client';

import {
    accessibleManagedAgentsState,
    dashboardReferencesState,
    pendingPromptState
} from '@/state/chat';
import { rightSidebarPanelState } from '@/state/project';

import { CollapsedSidebar } from './CollapsedSidebar';
import { ExpandedSidebar } from './ExpandedSidebar';
import { DMRightSidebarProvider } from './dm-right-sidebar';
import {
    AgentsPanel,
    AppsPanel,
    ArtifactsPanel,
    DisclosurePanel,
    PodcastsPanel,
    UploadsPanel
} from './panels';
import type { DMRightSideBarProps, SidebarPanelType } from './types';

const PANEL_TITLES: Record<Exclude<SidebarPanelType, null>, string> = {
    agents: 'Agents',
    apps: 'Charts',
    artifacts: 'Artifacts',
    podcasts: 'Podcasts',
    uploads: 'Uploaded/Embedded Files',
    disclosure: 'Disclosure'
};

function RightSidebarContent({ onClose }: DMRightSideBarProps) {
    const [activePanel, setActivePanel] = useRecoilState(
        rightSidebarPanelState
    );
    const sideView = useRecoilValue(sideViewState);
    const accessibleAgentCount = useRecoilValue(
        accessibleManagedAgentsState
    )?.length;
    const { config } = useConfig();
    const { pathname } = useLocation();
    const isFileUploadEnabled =
        config?.features?.spontaneous_file_upload?.enabled ?? false;

    const podcastElements = useMemo(() => {
        if (!sideView?.elements) return [];
        const filtered = sideView.elements.filter((el) => el.type === 'audio');

        return [...filtered].sort((a, b) => {
            if (a.forId !== b.forId) {
                return a.forId.localeCompare(b.forId);
            }
            return a.id.localeCompare(b.id);
        });
    }, [sideView?.elements]);

    const dashboardReferences = useRecoilValue(dashboardReferencesState);
    const setDashboardReferences = useSetRecoilState(dashboardReferencesState);
    const pendingPrompt = useRecoilValue(pendingPromptState);
    const { threadId, messages } = useChatMessages();
    const referencedDashboards = useMemo(() => {
        const found = new Map<string, string>();
        [
            ...messages
                .filter((message) => message.type === 'user_message')
                .flatMap((message) => referencedDashboardsOf(message.output)),
            ...(pendingPrompt ? referencedDashboardsOf(pendingPrompt) : []),
            ...dashboardReferences
                .filter((reference) => reference.kind === 'dashboard')
                .map((reference) => ({
                    id: reference.dashboardId,
                    title: reference.dashboardTitle
                }))
        ].forEach(({ id, title }) => {
            if (!found.has(id)) found.set(id, title);
        });
        return [...found].map(([id, title]) => ({ id, title }));
    }, [messages, pendingPrompt, dashboardReferences]);
    const referencedDashboardsRef = useRef(referencedDashboards);
    referencedDashboardsRef.current = referencedDashboards;
    const previousThreadIdRef = useRef(threadId);

    const allArtifactElements = useMemo(() => {
        const referenced = referencedDashboards.map(
            ({ id, title }): ICustomElement => ({
                id: `referenced-dashboard:${id}`,
                type: 'custom',
                name: 'DashboardPreview',
                display: 'side',
                props: { dashboardId: id, title },
                forId: '',
                mime: '',
                key: `referenced-dashboard:${id}`
            } as ICustomElement)
        );
        return [
            ...referenced,
            ...(sideView?.elements ?? []).filter(isArtifactElement)
        ];
    }, [sideView?.elements, referencedDashboards]);

    const artifactElements = useMemo(
        () => latestArtifactVersions(allArtifactElements),
        [allArtifactElements]
    );

    const artifactKeys = useMemo(
        () => artifactKeysOf(allArtifactElements),
        [allArtifactElements]
    );

    const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(
        null
    );
    const [uploadPreviewRequestId, setUploadPreviewRequestId] = useState<
        string | null
    >(null);
    const clearUploadPreviewRequest = useCallback(
        () => setUploadPreviewRequestId(null),
        []
    );

    const uploadElements = useMemo(() => {
        if (!sideView?.elements) return [];
        const fileTypes = ['pdf', 'file', 'image', 'video'];
        const filtered = sideView.elements.filter(
            (el) =>
                fileTypes.includes(el.type) && !isArtifactFile(el, artifactKeys)
        );

        return [...filtered].sort((a, b) => {
            if (a.forId !== b.forId) {
                return a.forId.localeCompare(b.forId);
            }
            return a.id.localeCompare(b.id);
        });
    }, [sideView?.elements, artifactKeys]);
    const appElements = useMemo(() => {
        if (!sideView?.elements) return [];
        return sideView.elements
            .filter(
                (el): el is ICustomElement =>
                    el.type === 'custom' &&
                    el.display === 'side' &&
                    ((el as any).name === 'mcpApp' ||
                        (el as any).name === 'HtmlPreview') &&
                    !isInlineApp(el)
            )
            .sort((a, b) => {
                const orderA = (a as any).props?.order ?? Infinity;
                const orderB = (b as any).props?.order ?? Infinity;
                return orderA - orderB;
            });
    }, [sideView?.elements]);

    const getItemCountForPanel = (
        panel: SidebarPanelType
    ): number | undefined => {
        switch (panel) {
            case 'agents':
                return accessibleAgentCount;
            case 'apps':
                return appElements.length;
            case 'artifacts':
                return artifactElements.length;
            case 'podcasts':
                return podcastElements.length;
            case 'uploads':
                return uploadElements.length;
            default:
                return undefined;
        }
    };

    const renderPanelContent = () => {
        switch (activePanel) {
            case 'agents':
                return <AgentsPanel />;
            case 'apps':
                return <AppsPanel elements={appElements} />;
            case 'artifacts':
                return (
                    <ArtifactsPanel
                        elements={artifactElements}
                        selectedId={resolveLatestArtifactId(
                            allArtifactElements,
                            artifactElements,
                            selectedArtifactId
                        )}
                        onSelect={setSelectedArtifactId}
                    />
                );
            case 'podcasts':
                return <PodcastsPanel elements={podcastElements} />;
            case 'uploads':
                return (
                    <UploadsPanel
                        elements={uploadElements}
                        previewRequestId={uploadPreviewRequestId}
                        onPreviewRequestHandled={clearUploadPreviewRequest}
                    />
                );
            case 'disclosure':
                return <DisclosurePanel />;
            default:
                return null;
        }
    };

    const handleClose = () => {
        setActivePanel(null);
        onClose?.();
    };

    const handleOpenPanel = (panel: SidebarPanelType) => {
        setActivePanel(panel === activePanel ? null : panel);
    };

    // Close uploads panel if file upload feature gets disabled
    useEffect(() => {
        if (!isFileUploadEnabled && activePanel === 'uploads') {
            setActivePanel(null);
        }
    }, [isFileUploadEnabled, activePanel, setActivePanel]);

    useEffect(() => {
        if (referencedDashboardsRef.current.length === 0) setActivePanel(null);
    }, [pathname, setActivePanel]);

    useEffect(() => {
        const previous = previousThreadIdRef.current;
        previousThreadIdRef.current = threadId;
        if (previous && threadId && previous !== threadId) {
            setDashboardReferences([]);
        }
    }, [threadId, setDashboardReferences]);

    // Auto-open apps panel when new app-based charts arrive
    // Skip in embedded mode — charts render inline in messages instead.
    const prevAppsCountRef = useRef<number | null>(null);
    useEffect(() => {
        if (isEmbeddedSidebar) return;
        const count = appElements.length;
        if (prevAppsCountRef.current === null) {
            // Initial load – just record count, don't auto-open
            prevAppsCountRef.current = count;
            return;
        }
        if (count > prevAppsCountRef.current) {
            const contentLoads = appElements.map((el) => {
                const props = (el as any).props ?? {};
                return (el as any).name === 'mcpApp' && props.resourceUri
                    ? prefetchMcpAppContent(props.resourceUri, props.serverUrl)
                    : Promise.resolve();
            });
            Promise.allSettled(contentLoads).then(() =>
                setActivePanel('apps')
            );
        }
        prevAppsCountRef.current = count;
    }, [appElements.length, setActivePanel, isEmbeddedSidebar]);

    const previousReferencedCountRef = useRef(referencedDashboards.length);
    useEffect(() => {
        const hadReferenced = previousReferencedCountRef.current > 0;
        previousReferencedCountRef.current = referencedDashboards.length;
        if (
            hadReferenced &&
            referencedDashboards.length === 0 &&
            allArtifactElements.length === 0 &&
            activePanel === 'artifacts'
        ) {
            setActivePanel(null);
        }
    }, [referencedDashboards.length]);

    const prevArtifactIdsRef = useRef<Set<string> | null>(null);
    useEffect(() => {
        const ids = new Set(allArtifactElements.map((el) => el.id));
        const previous = prevArtifactIdsRef.current;
        prevArtifactIdsRef.current = ids;
        if (isEmbeddedSidebar || previous === null) return;
        const threadSwitched = [...previous].some((id) => !ids.has(id));
        if (threadSwitched) return;
        const newest = allArtifactElements
            .filter((el) => !previous.has(el.id))
            .pop();
        if (newest) {
            setSelectedArtifactId(newest.id);
            setActivePanel('artifacts');
        }
    }, [allArtifactElements, setActivePanel, isEmbeddedSidebar]);

    const highlightChartCards = (elementIds?: string[]) => {
        if (!elementIds?.length) return;

        setTimeout(() => {
            let scrolled = false;
            for (const id of elementIds) {
                const el = document.querySelector(
                    `[data-app-element-id="${id}"]`
                );
                if (el) {
                    if (!scrolled) {
                        el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                        scrolled = true;
                    }
                    el.classList.add('chart-highlight');
                    setTimeout(
                        () => el.classList.remove('chart-highlight'),
                        2000
                    );
                }
            }
        }, 300);
    };

    useEffect(() => {
        const handleNewChat = () => {
            setActivePanel(null);
            setDashboardReferences([]);
        };
        window.addEventListener('newChat', handleNewChat);
        return () => window.removeEventListener('newChat', handleNewChat);
    }, [setActivePanel, setDashboardReferences]);

    useEffect(() => {
        if (isEmbeddedSidebar) return;
        const handleOpenCharts = (e: Event) => {
            const detail = (e as CustomEvent).detail as
                | { elementIds?: string[] }
                | undefined;
            setActivePanel('apps');
            highlightChartCards(detail?.elementIds);
        };

        window.addEventListener('openChartsSidebar', handleOpenCharts);
        return () =>
            window.removeEventListener('openChartsSidebar', handleOpenCharts);
    }, [setActivePanel, isEmbeddedSidebar]);

    useEffect(() => {
        if (isEmbeddedSidebar) return;
        const handleOpenApps = (e: Event) => {
            const detail = (e as CustomEvent).detail as
                | { elementIds?: string[] }
                | undefined;
            setActivePanel('apps');
            highlightChartCards(detail?.elementIds);
        };
        window.addEventListener('openAppsSidebar', handleOpenApps);
        return () =>
            window.removeEventListener('openAppsSidebar', handleOpenApps);
    }, [setActivePanel, isEmbeddedSidebar]);

    // Listen to openUploadsSidebar event from inline file attachment cards
    useEffect(() => {
        if (isEmbeddedSidebar || !isFileUploadEnabled) return;
        const handleOpenUploads = (e: Event) => {
            const detail = (e as CustomEvent).detail as
                | { elementId?: string }
                | undefined;
            setUploadPreviewRequestId(detail?.elementId ?? null);
            setActivePanel('uploads');
        };
        window.addEventListener('openUploadsSidebar', handleOpenUploads);
        return () =>
            window.removeEventListener('openUploadsSidebar', handleOpenUploads);
    }, [setActivePanel, isEmbeddedSidebar, isFileUploadEnabled]);

    useEffect(() => {
        if (isEmbeddedSidebar) return;
        const handleOpenArtifacts = (e: Event) => {
            const detail = (e as CustomEvent).detail as
                | { elementId?: string }
                | undefined;
            setSelectedArtifactId(detail?.elementId ?? null);
            setActivePanel('artifacts');
        };
        window.addEventListener('openArtifactsSidebar', handleOpenArtifacts);
        return () =>
            window.removeEventListener(
                'openArtifactsSidebar',
                handleOpenArtifacts
            );
    }, [setActivePanel, isEmbeddedSidebar]);

    const collapsedSidebar = (
        <CollapsedSidebar
            appCount={appElements.length}
            artifactCount={artifactElements.length}
            podcastCount={podcastElements.length}
            uploadCount={uploadElements.length}
            isFileUploadEnabled={isFileUploadEnabled}
            activePanel={activePanel}
            onOpenPanel={handleOpenPanel}
        />
    );

    if (!activePanel) {
        return collapsedSidebar;
    }

    return (
        <>
            <ExpandedSidebar
                title={PANEL_TITLES[activePanel]}
                itemCount={getItemCountForPanel(activePanel)}
                onClose={handleClose}
            >
                {renderPanelContent()}
            </ExpandedSidebar>
            {collapsedSidebar}
        </>
    );
}

export default function DMRightSideBar({ onClose }: DMRightSideBarProps) {
    const [activePanel, setActivePanel] = useRecoilState(
        rightSidebarPanelState
    );

    return (
        <DMRightSidebarProvider
            defaultOpen={false}
            open={activePanel !== null}
            onOpenChange={(open) => {
                if (!open) setActivePanel(null);
            }}
            className="w-auto! min-h-0!"
        >
            <RightSidebarContent onClose={onClose} />
        </DMRightSidebarProvider>
    );
}
